import React, { useEffect, useRef } from 'react';
import './Constellation.css';

const Constellation = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const starsRef = useRef([]);
  const mouseRef = useRef({ x: -1000, y: -1000 });

  // Function to check dark mode
  const checkDarkMode = () => {
    return document.documentElement.classList.contains('dark');
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initStars();
    };

    const initStars = () => {
      const stars = [];
      const starCount = 120;

      for (let i = 0; i < starCount; i++) {
        stars.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 2 + 1,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          opacity: Math.random() * 0.9 + 0.3,
          brightness: Math.random() * 0.5 + 0.5
        });
      }

      starsRef.current = stars;
    };

    // Mouse event handlers
    const handleMouseMove = (e) => {
      mouseRef.current = {
        x: e.clientX,
        y: e.clientY
      };
    };

    const handleMouseLeave = () => {
      mouseRef.current = { x: -1000, y: -1000 };
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Get current dark mode state
      const isDark = checkDarkMode();
      
      // Set background - DARKER in light mode for better contrast
      ctx.fillStyle = isDark ? '#0f172a' : '#e2e8f0';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const stars = starsRef.current;
      const mouse = mouseRef.current;

      // Update and draw stars
      stars.forEach(star => {
        // Move stars
        star.x += star.vx;
        star.y += star.vy;

        // Bounce off edges
        if (star.x < 0 || star.x > canvas.width) star.vx *= -1;
        if (star.y < 0 || star.y > canvas.height) star.vy *= -1;

        // Keep within bounds
        star.x = Math.max(0, Math.min(canvas.width, star.x));
        star.y = Math.max(0, Math.min(canvas.height, star.y));

        // Draw star with glow effect
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        
        // Create gradient for star glow
        const gradient = ctx.createRadialGradient(
          star.x, star.y, 0,
          star.x, star.y, star.radius * 2
        );
        
        if (isDark) {
          gradient.addColorStop(0, `rgba(255, 255, 255, ${star.opacity})`);
          gradient.addColorStop(0.5, `rgba(255, 255, 255, ${star.opacity * 0.5})`);
          gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        } else {
          // DARKER stars in light mode for better visibility
          gradient.addColorStop(0, `rgba(30, 41, 59, ${star.opacity})`);
          gradient.addColorStop(0.5, `rgba(30, 41, 59, ${star.opacity * 0.7})`);
          gradient.addColorStop(1, 'rgba(30, 41, 59, 0)');
        }
        
        ctx.fillStyle = gradient;
        ctx.fill();

        // Draw bright core
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius * 0.6, 0, Math.PI * 2);
        ctx.fillStyle = isDark 
          ? `rgba(255, 255, 255, ${star.opacity * 1.2})`
          : `rgba(30, 41, 59, ${star.opacity * 1.2})`;
        ctx.fill();
      });

      // Draw connections between stars - thicker and more visible
      ctx.strokeStyle = isDark ? 'rgba(99, 102, 241, 0.4)' : 'rgba(30, 41, 59, 0.3)';
      ctx.lineWidth = 1.2;

      for (let i = 0; i < stars.length; i++) {
        for (let j = i + 1; j < stars.length; j++) {
          const dx = stars[i].x - stars[j].x;
          const dy = stars[i].y - stars[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            const opacity = 1 - (distance / 150);
            ctx.strokeStyle = isDark 
              ? `rgba(99, 102, 241, ${opacity * 0.6})`
              : `rgba(30, 41, 59, ${opacity * 0.5})`;
            ctx.lineWidth = opacity * 2;
            ctx.beginPath();
            ctx.moveTo(stars[i].x, stars[i].y);
            ctx.lineTo(stars[j].x, stars[j].y);
            ctx.stroke();
          }
        }
      }

      // INTERACTIVE EFFECT: Draw connections from cursor to nearby stars
      if (mouse.x > 0 && mouse.y > 0) {
        stars.forEach(star => {
          const dx = mouse.x - star.x;
          const dy = mouse.y - star.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // Connect to stars within 250px of cursor
          if (distance < 250) {
            const opacity = 1 - (distance / 250);
            
            // Draw connection line from cursor to star
            ctx.strokeStyle = isDark 
              ? `rgba(139, 92, 246, ${opacity * 0.8})`
              : `rgba(59, 130, 246, ${opacity * 0.8})`;
            ctx.lineWidth = opacity * 3;
            ctx.beginPath();
            ctx.moveTo(mouse.x, mouse.y);
            ctx.lineTo(star.x, star.y);
            ctx.stroke();

            // Make connected stars glow brighter
            ctx.beginPath();
            ctx.arc(star.x, star.y, star.radius * (1 + opacity), 0, Math.PI * 2);
            const glowGradient = ctx.createRadialGradient(
              star.x, star.y, 0,
              star.x, star.y, star.radius * 3
            );
            if (isDark) {
              glowGradient.addColorStop(0, `rgba(139, 92, 246, ${opacity * 0.4})`);
              glowGradient.addColorStop(1, 'rgba(139, 92, 246, 0)');
            } else {
              glowGradient.addColorStop(0, `rgba(59, 130, 246, ${opacity * 0.4})`);
              glowGradient.addColorStop(1, 'rgba(59, 130, 246, 0)');
            }
            ctx.fillStyle = glowGradient;
            ctx.fill();

            // Slight attraction effect
            const attraction = 0.03 * opacity;
            star.vx += (mouse.x - star.x) * attraction * 0.01;
            star.vy += (mouse.y - star.y) * attraction * 0.01;
          }
        });

        // Draw cursor glow effect
        const gradient = ctx.createRadialGradient(
          mouse.x, mouse.y, 0,
          mouse.x, mouse.y, 80
        );
        if (isDark) {
          gradient.addColorStop(0, 'rgba(139, 92, 246, 0.6)');
          gradient.addColorStop(0.3, 'rgba(139, 92, 246, 0.3)');
          gradient.addColorStop(1, 'rgba(139, 92, 246, 0)');
        } else {
          gradient.addColorStop(0, 'rgba(59, 130, 246, 0.6)');
          gradient.addColorStop(0.3, 'rgba(59, 130, 246, 0.3)');
          gradient.addColorStop(1, 'rgba(59, 130, 246, 0)');
        }

        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 80, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Draw cursor center point
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = isDark ? 'rgba(139, 92, 246, 0.9)' : 'rgba(59, 130, 246, 0.9)';
        ctx.fill();
        
        // Draw inner glow
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 8, 0, Math.PI * 2);
        ctx.fillStyle = isDark ? 'rgba(139, 92, 246, 0.4)' : 'rgba(59, 130, 246, 0.4)';
        ctx.fill();
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    resizeCanvas();
    animate();

    // Add event listeners
    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas 
      ref={canvasRef} 
      className="constellation-canvas"
    />
  );
};

export default Constellation;